/* eslint-disable max-statements */
/* eslint-disable max-lines */
import { useCallback, useMemo, useContext } from 'react';
import { ControlledDateTimePickerContext } from '../../ControlledDateTimePickerCTX.js';
import type { DSControlledDateTimePickerInternalsT } from '../../sharedTypes.js';

interface UseOnClearClickR {
  onClearClick: DSControlledDateTimePickerInternalsT.OnDSBtnClick;
  onClearButtonKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown<HTMLButtonElement>;
  clearButtonRef: React.MutableRefObject<HTMLButtonElement | null>;
  withAnyPicker: boolean;
  withClearBtn: boolean;
}
export const useClearButton = (): UseOnClearClickR => {
  const {
    handleClearAll,
    clearButtonRef,
    withAnyPicker,
    withClearBtn,
    withAnyInputs,
    isWithTimeInputs,
    isWithDateInputs,
    trackFocusFirstSegment,
    trackFocusMeridiemInput,
    trackFocusYearInput,
    trackFocusPicker,
  } = useContext(ControlledDateTimePickerContext);

  const onClearClick: DSControlledDateTimePickerInternalsT.OnDSBtnClick = useCallback(
    (e) => {
      handleClearAll(e);
      trackFocusFirstSegment();
    },
    [handleClearAll, trackFocusFirstSegment],
  );
  const onHomeKeyDown = useCallback(() => {
    trackFocusFirstSegment();
  }, [trackFocusFirstSegment]);
  const onEndKeyDown = useCallback(() => {
    if (withAnyPicker) trackFocusPicker();
  }, [withAnyPicker, trackFocusPicker]);

  const onClearButtonKeyDown: DSControlledDateTimePickerInternalsT.OnKeyDown<HTMLButtonElement> = useCallback(
    (e) => {
      const { key } = e;
      const preventDefaultKeys = ['Home', 'End'];
      const stopPropagationKeys = ['Home', 'End'];
      if (preventDefaultKeys.includes(key)) e.preventDefault();
      if (stopPropagationKeys.includes(key)) e.stopPropagation();
      if (key === 'Home') onHomeKeyDown();
      if (key === 'End') onEndKeyDown();
      if (key === 'Backspace' && withAnyInputs) {
        if (isWithTimeInputs) trackFocusMeridiemInput();
        else if (isWithDateInputs) trackFocusYearInput();
      }
    },
    [
      isWithDateInputs,
      isWithTimeInputs,
      onEndKeyDown,
      onHomeKeyDown,
      trackFocusMeridiemInput,
      trackFocusYearInput,
      withAnyInputs,
    ],
  );

  return useMemo(
    () => ({ onClearClick, onClearButtonKeyDown, clearButtonRef, withAnyPicker, withClearBtn }),
    [onClearClick, onClearButtonKeyDown, clearButtonRef, withAnyPicker, withClearBtn],
  );
};
